import { Accordion } from "@base-ui/react/accordion";
import { ChevronRight } from "lucide-react";
import { useState, type CSSProperties, type FunctionComponent, type ReactNode } from "react";
import { cn } from "./cn";
import { FOCUS_RING_INSET } from "./focus";

type CollapseKey = string | number;

export interface CollapseItem {
  key: CollapseKey;
  label: ReactNode;
  children?: ReactNode;
  /** Rendered at the end of the header; clicks on it don't toggle the panel. */
  extra?: ReactNode;
  disabled?: boolean;
  showArrow?: boolean;
  /** Render the body before the panel is first opened. */
  forceRender?: boolean;
  className?: string;
}

export interface CollapseProps {
  items: CollapseItem[];
  activeKey?: CollapseKey | CollapseKey[];
  defaultActiveKey?: CollapseKey | CollapseKey[];
  /** Always receives the open keys as strings — also in `accordion` mode (antd passes a bare key there). */
  onChange?: (keys: string[]) => void;
  /** Only one panel open at a time. */
  accordion?: boolean;
  /** Transparent, borderless panels. */
  ghost?: boolean;
  bordered?: boolean;
  size?: "small" | "middle" | "large";
  expandIcon?: (panel: { isActive: boolean }) => ReactNode;
  expandIconPosition?: "start" | "end";
  /** Unmount bodies when closed (default: keep them mounted once opened). */
  destroyOnHidden?: boolean;
  className?: string;
  style?: CSSProperties;
}

const toKeys = (key: CollapseKey | CollapseKey[] | undefined): string[] =>
  key === undefined ? [] : (Array.isArray(key) ? key : [key]).map(String);

const headerSizeClass = {
  small: "px-3 py-2",
  middle: "px-4 py-3",
  large: "px-6 py-4 text-base",
} as const;

const bodySizeClass = {
  small: "p-3",
  middle: "p-4",
  large: "p-6",
} as const;

interface CollapseBodyProps {
  open: boolean;
  eager: boolean;
  children: ReactNode;
}

/** Defers rendering until the panel is first opened, then keeps the body mounted (antd default). */
const CollapseBody: FunctionComponent<CollapseBodyProps> = ({ open, eager, children }) => {
  const [visited, setVisited] = useState(open);
  if (open && !visited) setVisited(true);

  return eager || visited || open ? children : null;
};

/** Expandable panels (antd `Collapse` with `items`), built on Base UI `Accordion`. */
export const Collapse: FunctionComponent<CollapseProps> = ({
  items,
  activeKey,
  defaultActiveKey,
  onChange,
  accordion,
  ghost,
  bordered = true,
  size = "middle",
  expandIcon,
  expandIconPosition = "start",
  destroyOnHidden,
  className,
  style,
}) => {
  const [internalKeys, setInternalKeys] = useState(() => toKeys(defaultActiveKey));
  const openKeys = activeKey === undefined ? internalKeys : toKeys(activeKey);
  const framed = bordered && !ghost;

  return (
    <Accordion.Root
      value={openKeys}
      onValueChange={(value) => {
        const keys = value.map(String);
        setInternalKeys(keys);
        onChange?.(keys);
      }}
      multiple={!accordion}
      keepMounted={!destroyOnHidden}
      className={cn(
        "overflow-hidden text-sm text-noa",
        !ghost && "bg-noa-hover",
        framed && "rounded-lg border border-noa",
        className
      )}
      style={style}
    >
      {items.map((item) => {
        const key = String(item.key);
        const open = openKeys.includes(key);
        const icon =
          item.showArrow === false ? null : expandIcon ? (
            expandIcon({ isActive: open })
          ) : (
            <ChevronRight className={cn("size-3 shrink-0 transition-transform", open && "rotate-90")} />
          );

        return (
          <Accordion.Item
            key={key}
            value={key}
            disabled={item.disabled}
            className={cn(!ghost && "border-b border-noa last:border-b-0", item.className)}
          >
            <Accordion.Header className="flex items-center">
              <Accordion.Trigger
                className={cn(
                  "flex min-w-0 flex-1 cursor-pointer items-center gap-3 text-left",
                  FOCUS_RING_INSET,
                  "data-[disabled]:cursor-not-allowed data-[disabled]:text-noa-disabled",
                  expandIconPosition === "end" && "flex-row-reverse",
                  headerSizeClass[size],
                  item.extra !== undefined && "pr-2"
                )}
              >
                {icon}
                <span className="min-w-0 flex-1">{item.label}</span>
              </Accordion.Trigger>
              {item.extra !== undefined && (
                <div className={cn("shrink-0", headerSizeClass[size], "pl-0")}>{item.extra}</div>
              )}
            </Accordion.Header>
            <Accordion.Panel
              className={cn(
                "h-[var(--accordion-panel-height)] overflow-hidden transition-[height] duration-200",
                "data-[ending-style]:h-0 data-[starting-style]:h-0",
                !ghost && "bg-noa",
                framed && "border-t border-noa"
              )}
            >
              <div className={bodySizeClass[size]}>
                <CollapseBody open={open} eager={item.forceRender === true}>
                  {item.children}
                </CollapseBody>
              </div>
            </Accordion.Panel>
          </Accordion.Item>
        );
      })}
    </Accordion.Root>
  );
};
